import { ReactNode, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { PageContainer } from '@/components/global/PageContainer';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import {
  Menu,
  X,
  ChevronDown,
  Settings,
  LogOut,
  User,
  HelpCircle,
  FileText,
  MessageSquare,
  Bell,
  LayoutDashboard,
  Monitor,
  LayoutGrid,
  Package,
  MapPin,
  DollarSign,
  FileBarChart,
  Truck,
  RefreshCw,
} from 'lucide-react';
import { CatalogImportJobProvider } from '@/components/integrations/CatalogImportJobContext';
import { CatalogImportProgressBanner } from '@/components/integrations/CatalogImportProgressBanner';
import { ImpersonationBanner } from '@/components/auth/ImpersonationBanner';

interface NavDropdownItem {
  label: string;
  href: string;
  permission?: string;
}

interface NavItemConfig {
  label: string;
  href?: string;
  permission?: string;
  icon?: LucideIcon;
  items?: NavDropdownItem[];
  /** Extra path prefixes that keep this item highlighted. */
  activePathPrefixes?: string[];
  /** Estate Locations / Finance — tenant & platform admins only. */
  adminOnly?: boolean;
}

const ADMIN_NAV_ROLES = new Set(['tenantAdmin', 'admin', 'platformAdmin']);

function hasAdminNavAccess(roles: string[]): boolean {
  return roles.some((r) => ADMIN_NAV_ROLES.has(r));
}

const NAV_ITEMS: NavItemConfig[] = [
  { label: 'Dashboard', href: '/dashboard', permission: 'machines.read', icon: LayoutDashboard },
  { label: 'Machines', href: '/machines', permission: 'machines.read', icon: Monitor },
  {
    label: 'Refills',
    href: '/refills',
    permission: 'refills.read',
    icon: RefreshCw,
    activePathPrefixes: ['/refill-sessions'],
  },
  { label: 'Planograms', href: '/planograms', permission: 'planograms.read', icon: LayoutGrid },
  {
    label: 'Inventory',
    icon: Package,
    items: [
      { label: 'Products', href: '/products', permission: 'products.read' },
      { label: 'Categories', href: '/categories', permission: 'categories.read' },
      { label: 'Suppliers', href: '/suppliers', permission: 'suppliers.read' },
      { label: 'Stock Levels', href: '/stock', permission: 'inventory.read' },
      { label: 'Batches', href: '/batches', permission: 'inventory.read' },
      { label: 'Batch recall', href: '/inventory/batch-recall', permission: 'inventory.read' },
      { label: 'Goods Receipts', href: '/goods-receipts', permission: 'inventory.read' },
      { label: 'Transfers', href: '/locations/transfers', permission: 'inventory.read' },
      { label: 'Stock Takes', href: '/inventory/stock-takes', permission: 'inventory.read' },
      { label: 'Movements', href: '/inventory/movements', permission: 'inventory.read' },
    ],
  },
  {
    label: 'Locations',
    icon: MapPin,
    adminOnly: true,
    items: [
      { label: 'Overview', href: '/locations', permission: 'warehouses.read' },
      { label: 'Venues', href: '/locations/venues', permission: 'venues.read' },
    ],
  },
  {
    label: 'Finance',
    icon: DollarSign,
    adminOnly: true,
    items: [
      { label: 'Overview', href: '/finance', permission: 'finance.read' },
      { label: 'Commissions', href: '/finance/commissions', permission: 'finance.read' },
    ],
  },
  {
    label: 'Reports',
    icon: FileBarChart,
    items: [
      { label: 'Overview', href: '/reports', permission: 'reports.read' },
      { label: 'Out of Stock', href: '/reports/out-of-stock', permission: 'reports.read' },
      { label: 'Margin Analysis', href: '/reports/margins', permission: 'reports.read' },
      { label: 'Stock Value', href: '/reports/stock-value', permission: 'reports.read' },
      { label: 'Days of Cover', href: '/reports/days-of-cover', permission: 'reports.read' },
      { label: 'Sales', href: '/reports/sales', permission: 'reports.read' },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    items: [
      { label: 'Organization', href: '/settings', permission: 'settings.read' },
      { label: 'Users', href: '/settings/users', permission: 'users.read' },
      { label: 'Roles', href: '/settings/roles', permission: 'roles.read' },
      { label: 'Integrations', href: '/settings/integrations', permission: 'integrations.read' },
    ],
  },
];

/** Limited nav for spoke managers without hub/admin roles. */
const SPOKE_NAV_ITEMS: NavItemConfig[] = [
  { label: 'My stock', href: '/stock', permission: 'inventory.read', icon: Package },
  { label: 'Transfers', href: '/locations/transfers', permission: 'inventory.read', icon: Truck },
  { label: 'Stock counts', href: '/inventory/stock-takes', permission: 'inventory.read', icon: FileBarChart },
  {
    label: 'Refills',
    href: '/refills',
    permission: 'refills.read',
    icon: RefreshCw,
    activePathPrefixes: ['/refill-sessions'],
  },
];

function isSpokeScopedNav(roles: string[]): boolean {
  const isSpoke = roles.includes('spokeManager');
  const isUnrestricted = roles.some((r) =>
    ['hubManager', 'tenantAdmin', 'admin', 'platformAdmin'].includes(r)
  );
  return isSpoke && !isUnrestricted;
}

interface AdminShellProps {
  children: ReactNode;
}

function NavItemLink({
  href,
  label,
  icon: Icon,
  onClick,
  end,
  activePathPrefixes,
}: {
  href: string;
  label: string;
  icon?: LucideIcon;
  onClick?: () => void;
  end?: boolean;
  activePathPrefixes?: string[];
}) {
  const { pathname } = useLocation();
  const prefixActive = (activePathPrefixes ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

  return (
    <NavLink
      to={href}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded-[20px] px-4 py-2.5 text-base font-medium transition-colors',
          isActive || prefixActive
            ? 'bg-nav-accent-muted text-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )
      }
    >
      {Icon && <Icon className="h-[17px] w-[19px] shrink-0" />}
      <span>{label}</span>
    </NavLink>
  );
}

function NavItemDropdown({ item }: { item: NavItemConfig }) {
  const { hasPermission, user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';

  const visibleItems =
    item.items?.filter((subItem) => {
      if (!subItem.permission) return true;
      return isAdmin || hasPermission(subItem.permission);
    }) ?? [];

  if (visibleItems.length === 0) return null;

  const isActive = visibleItems.some((sub) => location.pathname === sub.href || location.pathname.startsWith(sub.href + '/'));
  const Icon = item.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'flex h-auto items-center gap-2 rounded-[20px] px-4 py-2.5 text-base font-medium',
            isActive
              ? 'bg-nav-accent-muted text-foreground hover:bg-nav-accent-muted hover:text-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          {Icon && <Icon className="h-[17px] w-[19px] shrink-0" />}
          <span>{item.label}</span>
          <ChevronDown className="ml-1 h-4 w-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[160px]">
        {visibleItems.map((subItem) => (
          <DropdownMenuItem key={subItem.href} asChild>
            <Link to={subItem.href} className="cursor-pointer">
              {subItem.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MainNavigation() {
  const { hasPermission, user, roles } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canSeeAdminMenus = hasAdminNavAccess(roles);
  const navItems = isSpokeScopedNav(roles) ? SPOKE_NAV_ITEMS : NAV_ITEMS;

  return (
    <nav className="hidden lg:flex shrink-0 items-center gap-6 whitespace-nowrap py-2 lg:ml-6" aria-label="Main navigation">
      {navItems.map((item) => {
        if (item.adminOnly && !canSeeAdminMenus) return null;
        if (item.permission && !isAdmin && !hasPermission(item.permission)) return null;

        if (item.href) {
          return (
            <NavItemLink
              key={item.href}
              href={item.href}
              label={item.label}
              icon={item.icon}
              activePathPrefixes={item.activePathPrefixes}
            />
          );
        }

        if (item.items) {
          const hasAnyPermission = isAdmin || item.items.some((sub) => !sub.permission || hasPermission(sub.permission));
          if (!hasAnyPermission) return null;
          return <NavItemDropdown key={item.label} item={item} />;
        }

        return null;
      })}
    </nav>
  );
}

function HelpMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <HelpCircle className="h-5 w-5" />
          <span className="sr-only">Help & Support</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuItem asChild>
          <Link to="/docs" className="cursor-pointer flex items-center">
            <FileText className="mr-2 h-4 w-4" />
            Documentation
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/support" className="cursor-pointer flex items-center">
            <MessageSquare className="mr-2 h-4 w-4" />
            Contact Support
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/changelog" className="cursor-pointer flex items-center">
            <Bell className="mr-2 h-4 w-4" />
            Release Notes
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const initials =
    `${user.firstName?.charAt(0) ?? ''}${user.lastName?.charAt(0) ?? ''}`.toUpperCase() ||
    user.email.charAt(0).toUpperCase();
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-14 items-center rounded-bl-[600px] rounded-tl-[600px] bg-nav-accent-muted pl-5 pr-5 py-2 outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="User menu"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground text-sm font-medium">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="font-semibold">{displayName}</span>
            <span className="text-xs text-muted-foreground font-normal">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/profile" className="cursor-pointer flex items-center">
            <User className="mr-2 h-4 w-4" />
            My Profile
          </Link>
        </DropdownMenuItem>
        {hasPermission('settings.read') && (
          <DropdownMenuItem asChild>
            <Link to="/settings" className="cursor-pointer flex items-center">
              <Settings className="mr-2 h-4 w-4" />
              Organization Settings
            </Link>
          </DropdownMenuItem>
        )}
        {hasPermission('users.read') && (
          <DropdownMenuItem asChild>
            <Link to="/settings/users" className="cursor-pointer flex items-center">
              <User className="mr-2 h-4 w-4" />
              Users & Roles
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNavGroup({
  item,
  visibleItems,
  onClose,
}: {
  item: NavItemConfig;
  visibleItems: NavDropdownItem[];
  onClose: () => void;
}) {
  const location = useLocation();
  const isActive = visibleItems.some(
    (sub) => location.pathname === sub.href || location.pathname.startsWith(sub.href + '/')
  );
  const [isExpanded, setIsExpanded] = useState(isActive);
  const Icon = item.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        className={cn(
          'flex w-full items-center gap-2 rounded-[20px] px-4 py-2.5 text-base font-medium transition-colors',
          isActive && !isExpanded
            ? 'bg-nav-accent-muted text-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )}
      >
        {Icon && <Icon className="h-[17px] w-[19px] shrink-0" />}
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', isExpanded && 'rotate-180')}
        />
      </button>
      {isExpanded && (
        <div className="flex flex-col pl-6">
          {visibleItems.map((subItem) => (
            <NavItemLink
              key={subItem.href}
              href={subItem.href}
              label={subItem.label}
              onClick={onClose}
              end={visibleItems.some(
                (other) => other !== subItem && other.href.startsWith(subItem.href + '/')
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNavigation({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { hasPermission, user, roles } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canSeeAdminMenus = hasAdminNavAccess(roles);
  const navItems = isSpokeScopedNav(roles) ? SPOKE_NAV_ITEMS : NAV_ITEMS;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white shadow-xl">
        <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
          <Link to="/dashboard" onClick={onClose} className="flex items-center gap-2">
            <img src="/express_b.svg" alt="Bright.Express" className="h-8 w-8 object-contain" />
            <span className="font-semibold">express</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>
        <nav
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain p-4"
          aria-label="Mobile navigation"
        >
          {navItems.map((item) => {
            if (item.adminOnly && !canSeeAdminMenus) return null;
            if (item.permission && !isAdmin && !hasPermission(item.permission)) return null;

            if (item.href) {
              return (
                <NavItemLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  icon={item.icon}
                  onClick={onClose}
                  activePathPrefixes={item.activePathPrefixes}
                />
              );
            }

            if (item.items) {
              const visibleItems = item.items.filter(subItem => {
                if (!subItem.permission) return true;
                return isAdmin || hasPermission(subItem.permission);
              });

              if (visibleItems.length === 0) return null;

              return (
                <MobileNavGroup
                  key={item.label}
                  item={item}
                  visibleItems={visibleItems}
                  onClose={onClose}
                />
              );
            }

            return null;
          })}
        </nav>
      </div>
    </div>
  );
}

function TopNavigation() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-40 h-14 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-full w-full min-w-0 items-center gap-2 pl-4 pr-0 lg:pl-[72px]">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden shrink-0"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open menu</span>
          </Button>
          <Link to="/dashboard" className="flex shrink-0 items-center gap-2">
            <img src="/express_b.svg" alt="Bright.Express" className="h-10 w-10 object-contain" />
            <span className="hidden font-semibold text-lg sm:inline">express</span>
          </Link>
          <div className="flex min-w-0 flex-1 items-center overflow-x-auto overflow-y-hidden scroll-smooth">
            <MainNavigation />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <HelpMenu />
            <UserMenu />
          </div>
        </div>
      </header>

      <MobileNavigation isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
    </>
  );
}

/** Mobile pick list is intentionally narrow; skip the global page width wrapper */
function isNarrowFullBleedRoute(pathname: string): boolean {
  return /\/machines\/[^/]+\/picklist$/.test(pathname);
}

export function AdminShell({ children }: AdminShellProps) {
  const { pathname } = useLocation();
  const usePageContainer = !isNarrowFullBleedRoute(pathname);

  return (
    <CatalogImportJobProvider>
      <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden">
        <ImpersonationBanner />
        <TopNavigation />
        <CatalogImportProgressBanner />
        <main className="min-w-0 flex-1 bg-muted/30">
          {usePageContainer ? <PageContainer>{children}</PageContainer> : children}
        </main>
      </div>
    </CatalogImportJobProvider>
  );
}

export default AdminShell;
